'use client';

import { reportClientError } from '@/lib/reportClientError';
import { recoverCurrentPage } from '@/lib/recoverToHome';
import { clearInstantRouteTransition } from '@/lib/instantRouteNavStore';
import { useEffect, useState } from 'react';

function isChunkLoadErrorMessage(message: string): boolean {
  return /Loading chunk \d+ failed/i.test(message) || /ChunkLoadError/i.test(message);
}

function isNextRouterStateErrorMessage(message: string): boolean {
  return (
    /parallelRoutes\.get/i.test(message) ||
    /Cannot read properties of null \(reading 'get'\)/i.test(message) ||
    /null is not an object \(evaluating 't\.parallelRoutes\.get'\)/i.test(message)
  );
}

function isWebpackRuntimeStaleChunkMessage(message: string, stack?: string): boolean {
  // Typical symptom of stale HTML/runtime vs chunk mismatch in Next/Webpack runtime.
  // Seen as: "Cannot read properties of undefined (reading 'call')" in webpack chunk.
  const s = `${message}\n${stack || ''}`;
  return (
    /Cannot read properties of undefined \(reading 'call'\)/i.test(message) &&
    /_next\/static\/chunks\/webpack|webpack-/i.test(s)
  );
}

export default function ClientErrorRecovery({
  boundary,
  error,
}: {
  boundary: 'global' | 'root';
  error: Error & { digest?: string };
}) {
  const [stay, setStay] = useState(false);

  useEffect(() => {
    try {
      reportClientError({ boundary, error });
    } catch {}

    const msg = String(error?.message || '');
    clearInstantRouteTransition();

    const retryKey = `hzc-pdp-retry:${window.location.pathname}`;
    const alreadyRetried = sessionStorage.getItem(retryKey) === '1';
    const onProductPath = window.location.pathname.startsWith('/urun/');
    // Production error.tsx hides the server message; one reload matches “refresh fixes it”.
    if (onProductPath && !alreadyRetried) {
      sessionStorage.setItem(retryKey, '1');
      window.location.reload();
      return;
    }

    const shouldAutoRecover =
      process.env.NODE_ENV === 'production' &&
      (isChunkLoadErrorMessage(msg) ||
        isNextRouterStateErrorMessage(msg) ||
        isWebpackRuntimeStaleChunkMessage(msg, error?.stack));

    if (shouldAutoRecover) {
      void recoverCurrentPage('chunk-load');
      return;
    }
    setStay(true);
  }, [boundary, error]);

  if (!stay) {
    return <div className="min-h-screen bg-hzc_beige_200" aria-busy="true" />;
  }

  return (
    <div className="min-h-screen bg-hzc_beige_200 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 text-center">
        <div className="text-lg font-semibold text-gray-900 mb-2">Bir sorun oluştu</div>
        <div className="text-sm text-gray-600 mb-4">
          Sayfa beklenmedik şekilde kesildi. Aynı sayfayı yenilemeyi dene; sepetin ve konumun durur.
        </div>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-full bg-hzc_purple_500 text-white px-5 py-2 text-sm"
          onClick={() => window.location.reload()}
        >
          Sayfayı yenile
        </button>
      </div>
    </div>
  );
}
